import React, { useState, useRef, useEffect, useCallback } from 'react';

function Dropdown({ value, onChange, options, placeholder, style, ...rest }) {
  const [open, setOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const ref = useRef(null);
  const listRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position list above if near bottom
  useEffect(() => {
    if (!open || !listRef.current || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 200) {
      listRef.current.style.bottom = '100%';
      listRef.current.style.top = 'auto';
      listRef.current.style.marginBottom = '4px';
    } else {
      listRef.current.style.top = '100%';
      listRef.current.style.bottom = 'auto';
      listRef.current.style.marginTop = '4px';
    }
  }, [open]);

  // Reset hovered index when opening
  useEffect(() => {
    if (open) {
      const idx = options.findIndex(o => (typeof o === 'string' ? o : o.value) === value);
      setHoveredIndex(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  const selected = options.find(o => (typeof o === 'string' ? o : o.value) === value);
  const label = selected ? (typeof selected === 'string' ? selected : selected.label) : (placeholder || 'Select...');

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoveredIndex(i => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoveredIndex(i => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (hoveredIndex >= 0 && hoveredIndex < options.length) {
        const opt = options[hoveredIndex];
        const v = typeof opt === 'string' ? opt : opt.value;
        onChange(v);
        setOpen(false);
      }
    }
  }, [open, hoveredIndex, options, onChange]);

  const ariaLabel = rest['aria-label'];

  return (
    <div ref={ref} style={{ position: 'relative', ...style }} onKeyDown={handleKeyDown}>
      <button type="button" onClick={() => setOpen(p => !p)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        style={{
        width: '100%', padding: '9px 32px 9px 12px', borderRadius: '6px',
        border: '1px solid rgba(123,104,238,0.15)',
        background: 'rgba(12,16,35,0.9)',
        color: value ? '#eee8ff' : 'rgba(220,210,255,0.4)',
        fontSize: '13px', textAlign: 'left', cursor: 'pointer',
        fontFamily: 'inherit', position: 'relative',
        transition: 'border-color 0.15s',
        borderColor: open ? 'rgba(123,104,238,0.35)' : 'rgba(123,104,238,0.15)',
      }}>
        {typeof label === 'string' ? label.toUpperCase() : label}
        <span style={{
          position: 'absolute', right: '10px', top: '50%', transform: `translateY(-50%) rotate(${open ? '180deg' : '0deg'})`,
          fontSize: '8px', color: 'rgba(123,104,238,0.5)', transition: 'transform 0.2s',
        }}>▼</span>
      </button>

      {open && (
        <div ref={listRef} role="listbox" style={{
          position: 'absolute', left: 0, right: 0, zIndex: 100,
          background: 'rgba(10,12,28,0.98)',
          border: '1px solid rgba(123,104,238,0.2)',
          borderRadius: '6px', overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5), 0 0 12px rgba(123,104,238,0.08)',
          animation: 'fsu 0.15s ease-out',
          maxHeight: '180px', overflowY: 'auto',
        }}>
          {options.map((opt, i) => {
            const v = typeof opt === 'string' ? opt : opt.value;
            const l = typeof opt === 'string' ? opt : opt.label;
            const isActive = v === value;
            const isHovered = i === hoveredIndex;
            return (
              <button key={i} type="button" role="option" aria-selected={isActive}
                onClick={() => { onChange(v); setOpen(false); }}
                onMouseEnter={() => setHoveredIndex(i)}
                style={{
                width: '100%', padding: '8px 12px',
                background: isHovered ? 'rgba(123,104,238,0.08)' : isActive ? 'rgba(123,104,238,0.12)' : 'transparent',
                border: 'none', borderBottom: i < options.length - 1 ? '1px solid rgba(123,104,238,0.06)' : 'none',
                color: isActive ? '#a5b4fc' : '#ccc8e8',
                fontSize: '12px', textAlign: 'left', cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.5px',
                transition: 'background 0.15s',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                {isActive && <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#7b68ee', boxShadow: '0 0 4px #7b68ee' }} />}
                {(l || '').toUpperCase()}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
